import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, type FormEvent, useState } from "react";

import { AppLayout } from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface AlumniProfile {
  id: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  cohortYear: number;
  linkedinUrl: string | null;
}

interface AddProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  role: string;
  cohortYear: string;
  linkedinUrl: string;
}

const EMPTY_FORM: AddProfileForm = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  role: "",
  cohortYear: "",
  linkedinUrl: "",
};

const COHORT_YEARS = [2022, 2023, 2024, 2025, 2026];

const ALUMNI_UNLOCK_KEY = "tesla-alumni-unlocked-at";
const ALUMNI_PASSWORD = "teslaprogram";
const ALUMNI_UNLOCK_TTL_MS = 24 * 60 * 60 * 1000; // re-prompt for the password every 24 hours

function isAlumniUnlocked(): boolean {
  const unlockedAt = Number(localStorage.getItem(ALUMNI_UNLOCK_KEY));
  return Boolean(unlockedAt) && Date.now() - unlockedAt < ALUMNI_UNLOCK_TTL_MS;
}

async function fetchAlumni(search: string, cohortYear: string): Promise<AlumniProfile[]> {
  const params = new URLSearchParams();
  if (search.trim()) {
    params.set("search", search.trim());
  }
  if (cohortYear) {
    params.set("cohort_year", cohortYear);
  }

  const res = await fetch(`/api/alumni${params.toString() ? `?${params.toString()}` : ""}`);
  if (!res.ok) {
    throw new Error("Failed to load alumni");
  }
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data)) {
    return [];
  }
  return data as AlumniProfile[];
}

async function createAlumni(form: AddProfileForm): Promise<AlumniProfile> {
  const res = await fetch("/api/alumni", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      company: form.company,
      role: form.role,
      cohort_year: parseInt(form.cohortYear, 10),
      linkedin_url: form.linkedinUrl || null,
    }),
  });
  if (!res.ok) {
    throw new Error("Failed to create profile");
  }
  return (await res.json()) as AlumniProfile;
}

function AlumniCard({ profile }: { profile: AlumniProfile }) {
  return (
    <div className="border-border flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm">
      <div>
        <p className="text-foreground font-semibold">
          {profile.firstName} {profile.lastName}
        </p>
        <p className="text-foreground/80 text-sm">{profile.role}</p>
        <p className="text-muted text-sm">{profile.company}</p>
      </div>

      <div className="flex items-center justify-between">
        <span className="bg-accent-light text-accent rounded-full px-2.5 py-0.5 text-xs font-medium">
          Cohort {profile.cohortYear}
        </span>

        {profile.linkedinUrl ? (
          <a
            href={profile.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary text-sm font-medium hover:underline"
          >
            LinkedIn →
          </a>
        ) : (
          <span className="text-muted text-sm">No LinkedIn</span>
        )}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="border-border animate-pulse rounded-2xl border bg-white p-5">
      <div className="bg-muted/20 mb-2 h-4 w-3/4 rounded" />
      <div className="bg-muted/20 mb-1 h-3 w-1/2 rounded" />
      <div className="bg-muted/20 h-3 w-2/3 rounded" />
    </div>
  );
}

function AddProfileDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AddProfileForm>(EMPTY_FORM);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: createAlumni,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alumni"] });
      setSuccess(true);
    },
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate(form);
  };

  const handleClose = (): void => {
    onOpenChange(false);
    setForm(EMPTY_FORM);
    setSuccess(false);
    mutation.reset();
  };

  const updateField =
    (field: keyof AddProfileForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add your profile</DialogTitle>
          <DialogDescription>
            Join the Tesla Program alumni directory and connect with fellow innovation leaders.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <p className="text-foreground font-semibold">Profile added!</p>
            <p className="text-muted mt-2 text-sm">
              Your profile is now visible in the alumni directory.
            </p>
            <Button onClick={handleClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">
                  First name <span className="text-destructive">*</span>
                </label>
                <Input
                  required
                  value={form.firstName}
                  onChange={updateField("firstName")}
                  placeholder="Jane"
                />
              </div>
              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">
                  Last name <span className="text-destructive">*</span>
                </label>
                <Input
                  required
                  value={form.lastName}
                  onChange={updateField("lastName")}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div>
              <label className="text-foreground mb-1 block text-sm font-medium">
                Email <span className="text-destructive">*</span>
              </label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={updateField("email")}
                placeholder="jane.smith@example.com"
              />
              <p className="text-muted mt-1 text-xs">
                Used to find and update your own entry later — not shown publicly.
              </p>
            </div>

            <div>
              <label className="text-foreground mb-1 block text-sm font-medium">
                Company <span className="text-destructive">*</span>
              </label>
              <Input
                required
                value={form.company}
                onChange={updateField("company")}
                placeholder="Your organisation"
              />
            </div>

            <div>
              <label className="text-foreground mb-1 block text-sm font-medium">
                Role <span className="text-destructive">*</span>
              </label>
              <Input
                required
                value={form.role}
                onChange={updateField("role")}
                placeholder="Innovation Manager"
              />
            </div>

            <div>
              <label className="text-foreground mb-1 block text-sm font-medium">
                Cohort year <span className="text-destructive">*</span>
              </label>
              <select
                required
                value={form.cohortYear}
                onChange={updateField("cohortYear")}
                className="border-border bg-background text-foreground focus:ring-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              >
                <option value="">Select year…</option>
                {COHORT_YEARS.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-foreground mb-1 block text-sm font-medium">LinkedIn URL</label>
              <Input
                type="url"
                value={form.linkedinUrl}
                onChange={updateField("linkedinUrl")}
                placeholder="https://linkedin.com/in/…"
              />
            </div>

            {mutation.isError && (
              <p className="text-destructive text-sm">Something went wrong. Please try again.</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Saving…" : "Add profile"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AlumniPasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (value === ALUMNI_PASSWORD) {
      localStorage.setItem(ALUMNI_UNLOCK_KEY, String(Date.now()));
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <div className="mx-auto flex max-w-sm flex-col items-center gap-4 px-6 py-24 text-center">
      <h1 className="font-heading text-foreground text-xl font-bold">Alumni Directory</h1>
      <p className="text-muted text-sm">
        This page is password protected. Enter the program password to continue.
      </p>
      <form onSubmit={handleSubmit} className="w-full space-y-3">
        <Input
          type="password"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(false);
          }}
          placeholder="Password"
          autoFocus
        />
        {error && <p className="text-destructive text-sm">Incorrect password.</p>}
        <Button type="submit" className="w-full">
          Unlock
        </Button>
      </form>
    </div>
  );
}

export function AlumniDirectoryPage() {
  const [unlocked, setUnlocked] = useState(isAlumniUnlocked);
  const [search, setSearch] = useState("");
  const [cohortYear, setCohortYear] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["alumni", search, cohortYear],
    queryFn: () => fetchAlumni(search, cohortYear),
    placeholderData: (prev) => prev,
    enabled: unlocked,
  });

  const alumni = data ?? [];

  if (!unlocked) {
    return (
      <AppLayout>
        <AlumniPasswordGate onUnlock={() => setUnlocked(true)} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-primary mb-2 text-xs font-semibold tracking-[0.24em] uppercase">
              Tesla Program
            </p>
            <h1 className="font-heading text-foreground text-2xl font-bold">Alumni Directory</h1>
            <p className="text-muted mt-2 text-sm">
              Connect with fellow innovation leaders from across the cohort.
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>Add your profile</Button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <svg
              className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <Input
              placeholder="Search by name, company, or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <select
            value={cohortYear}
            onChange={(e) => setCohortYear(e.target.value)}
            className="border-border bg-background text-foreground focus:ring-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
          >
            <option value="">All cohorts</option>
            {COHORT_YEARS.map((y) => (
              <option key={y} value={String(y)}>
                Cohort {y}
              </option>
            ))}
          </select>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex min-h-[30vh] flex-col items-center justify-center text-center">
            <p className="text-foreground font-semibold">Couldn&apos;t load alumni</p>
            <p className="text-muted mt-2 text-sm">
              There was an error loading the directory. Please try again.
            </p>
          </div>
        )}

        {!isLoading && !isError && alumni.length === 0 && (
          <div className="flex min-h-[30vh] flex-col items-center justify-center text-center">
            <p className="text-foreground font-semibold">No alumni found</p>
            <p className="text-muted mt-2 text-sm">
              {search || cohortYear
                ? "Try adjusting your search or filter."
                : "Be the first to add your profile to the directory."}
            </p>
            {(search || cohortYear) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCohortYear("");
                }}
                className="text-primary mt-3 text-sm hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {!isLoading && !isError && alumni.length > 0 && (
          <>
            <p className="text-muted mb-4 text-sm">
              {alumni.length} alumni{alumni.length !== 1 ? "s" : ""}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {alumni.map((profile) => (
                <AlumniCard key={profile.id} profile={profile} />
              ))}
            </div>
          </>
        )}
      </div>

      <AddProfileDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </AppLayout>
  );
}
